import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Contexts
import { AuthProvider } from "./context/AuthContext";
import { WebSocketProvider } from "./context/WebSocketContext";

// Query Client
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './config/queryClient';

// Components
import Sidebar from './Components/Sidebar';
import Navbar from './Components/Navbar';
import PrivateRoute from './Components/PrivateRoute';

// CSS
import './Css/Common.css';
import './Css/App.css';

// Routes - Home
import Dashboard from './routes/home/Dashboard';
import Nest from './routes/home/Nest';
import Tab from './routes/home/Tab';

// Routes - Authentication
import LoginScreen from './routes/LoginScreen';
import LandingPage from './routes/LandingPage';
import NotFound from './routes/NotFound';

// Routes - Devices
import Printers from './routes/devices/Printers';
import Nestlink from './routes/devices/Nestlink';
import Other from './routes/devices/Other';

// Routes - Files
import Documents from './routes/files/Documents';
import SmartGroups from './routes/files/SmartGroups';
import Temporary from './routes/files/Temporary';

// Routes - Macros

import CreateMacro from './routes/macros/CreateMacro';
import ManageMacros from './routes/macros/ManageMacros';

// Routes - Automations

import Workflows from './routes/automations/Workflows';
import Triggers from './routes/automations/Triggers';
import Schedules from './routes/automations/Schedules';

// Routes - History

import Recent from './routes/history/Recent';
import Logs from './routes/history/Logs';

// Routes - Filament

import PLA from './routes/filament/PLA';
import ABS from './routes/filament/ABS';
import PETG from './routes/filament/PETG';

// Routes - Statistics

import Usage from './routes/statistics/Usage';
import Performance from './routes/statistics/Performance';

// Routes - Options

import Settings from './routes/options/Settings';
import Preferences from './routes/options/Preferences';



function AppLayout({ children }) {
  const location = useLocation();

  // Ścieżki chronione (PrivateRoute)
  const protectedPaths = [
    "/home/dashboard",
    "/home/nest",
    "/home/tab",
    "/devices/printers",
    "/devices/nestlink",
    "/devices/other",
    "/files/documents",
    "/files/smartgroups",
    "/files/temporary",
    "/macros/create",
    "/macros/manage",
    "/automations/workflows",
    "/automations/triggers",
    "/automations/schedules",
    "/history/recent",
    "/history/logs",
    "/filament/pla",
    "/filament/abs",
    "/filament/petg",
    "/statistics/usage",
    "/statistics/performance",
    "/options/settings",
    "/options/preferences",
  ];

  // Sprawdź, czy aktualna ścieżka jest chroniona
  const shouldShowSidebarAndNavbar = protectedPaths.includes(location.pathname);

  return (
    <div className="app-layout">
      {shouldShowSidebarAndNavbar && <Sidebar />}
      {shouldShowSidebarAndNavbar ? (
        <div className="main-content">
          <Navbar />
          {children}
        </div>
      ) : (
        <>{children}</>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <WebSocketProvider>
            <AppLayout>
              <Routes>
                {/* Publiczne trasy */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginScreen />} />

                {/* Chronione trasy */}
                <Route
                  path="/home/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/home/nest"
                  element={
                    <PrivateRoute>
                      <Nest />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/home/tab"
                  element={
                    <PrivateRoute>
                      <Tab />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/devices/printers"
                  element={
                    <PrivateRoute>
                      <Printers />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/devices/nestlink"
                  element={
                    <PrivateRoute>
                      <Nestlink />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/devices/other"
                  element={
                    <PrivateRoute>
                      <Other />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/files/documents"
                  element={
                    <PrivateRoute>
                      <Documents />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/files/smartgroups"
                  element={
                    <PrivateRoute>
                      <SmartGroups />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/files/temporary"
                  element={
                    <PrivateRoute>
                      <Temporary />
                    </PrivateRoute>
                  }
                />
            
                <Route
                  path="/macros/create"
                  element={
                    <PrivateRoute>
                      <CreateMacro />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/macros/manage"
                  element={
                    <PrivateRoute>
                      <ManageMacros />
                    </PrivateRoute>
                  }
                />
              
                <Route
                  path="/automations/workflows"
                  element={
                    <PrivateRoute>
                      <Workflows />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/automations/triggers"
                  element={
                    <PrivateRoute>
                      <Triggers />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/automations/schedules"
                  element={
                    <PrivateRoute>
                      <Schedules />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/history/recent"
                  element={
                    <PrivateRoute>
                      <Recent />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/history/logs"
                  element={
                    <PrivateRoute>
                      <Logs />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/filament/pla"
                  element={
                    <PrivateRoute>
                      <PLA />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/filament/abs"
                  element={
                    <PrivateRoute>
                      <ABS />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/filament/petg"
                  element={
                    <PrivateRoute>
                      <PETG />
                    </PrivateRoute>
                  }
                />
              
                <Route
                  path="/statistics/usage"
                  element={
                    <PrivateRoute>
                      <Usage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/statistics/performance"
                  element={
                    <PrivateRoute>
                      <Performance />
                    </PrivateRoute>
                  }
                />
              
                <Route
                  path="/options/settings"
                  element={
                    <PrivateRoute>
                      <Settings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/options/preferences"
                  element={
                    <PrivateRoute>
                      <Preferences />
                    </PrivateRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AppLayout>
          </WebSocketProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </AuthProvider>
      </QueryClientProvider>            
    </Router>
  );
}

export default App;